import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { LoginPage } from './pages/LoginPage'
import { isDemoMode, isSupabaseConfigured, supabase } from './lib/supabase'
import { PORT_BASE, V1_REDIRECTS, appPath } from './app/routes'
import { AppRoutes } from './app/AppRoutes'
import { Starfield } from './app/Starfield'
import { BrandMark, BrandWordmark } from './app/Brand'

/**
 * Árvore de rotas.
 *
 * Desde o corte da fase 7 (21/09/2026) existe uma interface só: a do Zênite,
 * na raiz. O que sobrou da V1 são endereços — os caminhos antigos e o prefixo
 * `/v2` do porte continuam abrindo, redirecionando para a superfície
 * equivalente, para que link guardado e favorito não morram (AGENTS §3.2).
 */
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {V1_REDIRECTS.map((entry) => (
        <Route
          key={entry.from}
          path={`${entry.from}/*`}
          element={<Navigate to={appPath(entry.to)} replace />}
        />
      ))}
      {/* O painel da V1 e o editor de fluxo dela não têm equivalente próprio:
          o processo passou a viver dentro do funil (decisão de 17/09/2026). */}
      <Route path="/v1/*" element={<Navigate to={appPath()} replace />} />
      <Route path="/flows/*" element={<Navigate to={appPath('funis')} replace />} />
      <Route path={`${PORT_BASE}/*`} element={<RedirecionaDoPorte />} />
      <Route path="/*" element={<AuthGate><AppRoutes /></AuthGate>} />
    </Routes>
  )
}

/** `/v2/leads?x=1` vira `/leads?x=1`: o prefixo do porte some, o resto fica. */
function RedirecionaDoPorte() {
  const params = useParams()
  const resto = params['*'] ?? ''
  return <Navigate to={`${appPath(resto)}${window.location.search}${window.location.hash}`} replace />
}

/**
 * Quanto tempo a espera da sessão pode ficar calada.
 *
 * `getSession` responde na hora quando o token guardado ainda vale; a demora
 * mora na renovação, que é rede — e rede pode não voltar. Passado o teto, a
 * tela para de só girar e passa a oferecer saída (§18).
 */
const SESSION_WAIT_CEILING_MS = 6000

function AuthGate({children}:{children:ReactNode}){
  const known = isDemoMode || !isSupabaseConfigured
  const [ready,setReady]=useState(known)
  const [authenticated,setAuthenticated]=useState(known)
  /** Passou do teto sem resposta: a espera vira erro com caminho de volta. */
  const [tooSlow,setTooSlow]=useState(false)
  /** Cada tentativa remonta o efeito e zera o teto. */
  const [attempt,setAttempt]=useState(0)
  const authRevisionRef = useRef(0)

  useEffect(()=>{
    if(isDemoMode||!supabase)return
    let active = true
    setTooSlow(false)
    const{data}=supabase.auth.onAuthStateChange((_event,session)=>{
      authRevisionRef.current += 1
      if (!active) return
      setAuthenticated(Boolean(session))
      setReady(true)
    })
    const initialRevision = authRevisionRef.current
    supabase.auth.getSession().then(({data:sessionData})=>{
      if (!active || authRevisionRef.current !== initialRevision) return
      setAuthenticated(Boolean(sessionData.session))
      setReady(true)
    })
    return()=>{
      active = false
      authRevisionRef.current += 1
      data.subscription.unsubscribe()
    }
  },[attempt])

  useEffect(()=>{
    if(ready)return
    const id = window.setTimeout(()=>setTooSlow(true), SESSION_WAIT_CEILING_MS)
    return ()=>window.clearTimeout(id)
  },[ready,attempt])

  if(!ready)
    return <SessionWait slow={tooSlow} onRetry={()=>setAttempt((value)=>value+1)} />
  return authenticated?children:<Navigate to="/login" replace/>
}

/**
 * Espera da sessão.
 *
 * É a primeira tela de quem abre o produto, então ela é o produto: marca,
 * estrelas e o mesmo fundo do login. Enquanto a resposta vem, diz o que está
 * fazendo; quando demora demais, diz por quê e oferece as duas saídas reais —
 * tentar de novo ou entrar outra vez.
 */
function SessionWait({slow,onRetry}:{slow:boolean;onRetry:()=>void}){
  return (
    <div className="cx-session-wait">
      <Starfield className="cx-stars" />
      <div className="cx-session-card" role="status" aria-live="polite">
        <span className="cx-brand">
          <BrandMark size={34} />
          <BrandWordmark />
        </span>
        {slow ? (
          <>
            <p className="cx-session-title">A sua sessão está demorando a responder</p>
            <p className="cx-session-sub">
              O servidor de autenticação não respondeu a tempo. Seus dados estão a salvo; é só
              tentar de novo ou entrar outra vez.
            </p>
            <div className="cx-session-actions">
              <button type="button" className="cx-btn" data-variant="primary" onClick={onRetry}>
                Tentar de novo
              </button>
              <a className="cx-btn" data-variant="secondary" href="/login">
                Entrar outra vez
              </a>
            </div>
          </>
        ) : (
          <>
            <span className="cx-session-orbit" aria-hidden="true" />
            <p className="cx-session-title">Preparando o COSMOS</p>
            <p className="cx-session-sub">Conferindo a sua sessão.</p>
          </>
        )}
      </div>
    </div>
  )
}
